import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import React, { useState } from "react";
import { EmailLogAPIError, useGetEmailLogEntryQuery } from "users/super-users/emails/_api/email-log-api";
import type EmailLogEntry from "users/super-users/emails/email-log-entry";
import { buildEmailPreviewDocument } from "users/super-users/emails/email-preview-document";
import { formatEmailLogInstant } from "users/super-users/_views/emails/email-log-format";

export const EMAIL_LOG_ENTRY_REFUSED = "You do not have permission to read the email log.";
export const EMAIL_LOG_ENTRY_MISSING = "This email is no longer in the log. It may have passed its retention period.";
export const EMAIL_LOG_ENTRY_FAILED = "Unable to load this email.";
export const EMAIL_LOG_CAPTURED_BANNER = "Captured, not sent. This email was recorded instead of being sent, so nobody received it.";

const Headers = styled("dl")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.5, 2),
  gridTemplateColumns: "max-content minmax(0, 1fr)",
  margin: 0,
  "& dt": { color: theme.palette.text.secondary, fontWeight: 500 },
  "& dd": { margin: 0, overflowWrap: "anywhere" },
}));
const PreviewFrame = styled("iframe")(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  height: "28rem",
  width: "100%",
}));
const PlainText = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  margin: 0,
  maxHeight: "28rem",
  overflow: "auto",
  padding: theme.spacing(1.5),
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));

enum BodyTab {
  Html = "html",
  Text = "text",
}

function entryErrorMessage(error: unknown): string {
  const response = error instanceof EmailLogAPIError ? error.error : error;
  const status = getApiErrorStatus(response);
  if (status === HttpStatusCode.Forbidden) return EMAIL_LOG_ENTRY_REFUSED;
  if (status === HttpStatusCode.NotFound) return EMAIL_LOG_ENTRY_MISSING;
  return EMAIL_LOG_ENTRY_FAILED;
}

/** One logged email in full: its headers, what happened to it, and its HTML and text (#6922). */
export default function EmailLogEntryDialog(props: Readonly<{ entryId: string; onClose: () => void }>) {
  const entryQuery = useGetEmailLogEntryQuery(props.entryId);
  const entry = entryQuery.data;

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={props.onClose} aria-labelledby="email-log-entry-title">
      <DialogTitle id="email-log-entry-title">{entry?.subject ?? "Email"}</DialogTitle>
      <DialogContent dividers>
        {entryQuery.isLoading && <CircularProgress aria-label="Loading email" />}
        {entryQuery.isError && <Alert severity="error">{entryErrorMessage(entryQuery.error)}</Alert>}
        {entry && <EmailLogEntryDetail entry={entry} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function EmailLogEntryDetail(props: Readonly<{ entry: EmailLogEntry }>) {
  const { entry } = props;
  const [bodyTab, setBodyTab] = useState(entry.htmlBody ? BodyTab.Html : BodyTab.Text);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {entry.isCaptured && <Alert severity="info">{EMAIL_LOG_CAPTURED_BANNER}</Alert>}
      {entry.outcome === "Failed" && (
        <Alert severity="error">Not sent. {entry.error ?? "The provider gave no reason."}</Alert>
      )}
      <Headers aria-label="Email headers">
        <dt>Recorded</dt>
        <dd>{formatEmailLogInstant(entry.recordedAt)}</dd>
        <dt>Environment</dt>
        <dd>{entry.environment}</dd>
        <dt>Outcome</dt>
        <dd>{entry.outcome}</dd>
        <dt>Template</dt>
        <dd>{entry.template ?? "—"}</dd>
        <dt>From</dt>
        <dd>{entry.sender ?? "—"}</dd>
        <dt>To</dt>
        <dd>{entry.recipient ?? "—"}</dd>
        <dt>Cc</dt>
        <dd>{entry.carbonCopy || "—"}</dd>
        <dt>Bcc</dt>
        <dd>{entry.blindCarbonCopy || "—"}</dd>
        <dt>Subject</dt>
        <dd>{entry.subject ?? "—"}</dd>
        <dt>Message id</dt>
        <dd>{entry.providerMessageId ?? "—"}</dd>
        <dt>Belongs to</dt>
        <dd>{entry.correlationKind ? `${entry.correlationKind} ${entry.correlationId?.value ?? ""}` : "—"}</dd>
      </Headers>
      {entry.linksMasked && (
        <Alert severity="warning">
          Sign-in and invitation tokens are masked in this copy, so its links do not work.
        </Alert>
      )}
      {entry.bodiesRemovedAt && (
        <Alert severity="info">
          The HTML and text were removed {formatEmailLogInstant(entry.bodiesRemovedAt)}, at the end of their retention period.
        </Alert>
      )}
      {!entry.bodiesRemovedAt && (
        <>
          <Tabs value={bodyTab} onChange={(_event, value: BodyTab) => setBodyTab(value)} aria-label="Email body">
            <Tab label="HTML" value={BodyTab.Html} disabled={!entry.htmlBody} />
            <Tab label="Text" value={BodyTab.Text} disabled={!entry.textBody} />
          </Tabs>
          {bodyTab === BodyTab.Html && entry.htmlBody && <EmailHtmlPreview html={entry.htmlBody} />}
          {bodyTab === BodyTab.Text && <PlainText aria-label="Email text">{entry.textBody ?? ""}</PlainText>}
        </>
      )}
    </Box>
  );
}

/**
 * The letter's HTML in a frame that can run nothing and follow nothing. Remote images stay blocked
 * until the viewer asks for them, because loading one tells the image's host the letter was opened.
 */
function EmailHtmlPreview(props: Readonly<{ html: string }>) {
  const [allowRemoteImages, setAllowRemoteImages] = useState(false);
  const preview = buildEmailPreviewDocument(props.html, { allowRemoteImages });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {preview.remoteImageCount > 0 && !allowRemoteImages && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setAllowRemoteImages(true)}>
              Load images
            </Button>
          }
        >
          {preview.remoteImageCount === 1 ? "1 remote image is" : `${preview.remoteImageCount} remote images are`} blocked.
        </Alert>
      )}
      {/* sandbox="" grants nothing: no scripts, forms, pop-ups or navigation of this page. */}
      <PreviewFrame title="Email HTML" sandbox="" referrerPolicy="no-referrer" srcDoc={preview.html} />
      <Typography variant="caption" color="text.secondary">
        Links in this preview do not open.
      </Typography>
    </Box>
  );
}
